import React, { useEffect, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";

import { Container } from "@mui/material";

import {
  Button,
  ButtonProps,
  Divider,
  Modal,
  Popover,
  Table,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { IconChevronLeft, IconReload, IconShare } from "@tabler/icons-react";

import dayjs from "dayjs";

import DisplayAlert from "../../components/DisplayAlert";
import MegaLoader from "../../components/MegaLoader";
import TabularData from "../../components/TabularData";
import VehicleForm from "../../components/VehicleForm";

import { Endpoints } from "../../lib/Endpoints";
import { NonPaginatedResponse } from "../../lib/Responses";
import { VehicleDocumentTypes, VehicleProps, VinAPIResponse } from "../../lib/Types";
import { usePerformRequest } from "../../lib/usePerformRequest";

import { Colors, getVehicleValueFromVinVariable, VehicleDocuments } from "../../lib/Data";

import { useForm, UseFormReturnType } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import AnimateOnLoad from "../../components/AnimateOnLoad";
import {
  DefaultErrorNotification,
  generateDocument,
  getDocumentTitle,
  getFinancialValueFromNumeric,
  validateEmail,
  validateVIN,
} from "../../lib/Methods";
import "./styles.scss";

const SingleVehicle: React.FC = () => {
  const navigate = useNavigate();
  const { vehicleID } = useParams();
  const {
    data: r,
    isLoading: isLoadingRequest,
    reloadData: reloadRequest,
  } = usePerformRequest<NonPaginatedResponse<VehicleProps>>({
    method: "GET",
    route: Endpoints.GetSingleVehicleDetails(vehicleID as string),
  });
  const request = r && r.data && r.data.id ? r.data : undefined;
  const {
    data: rVin,
    isLoading: isLoadingVinDetails,
    reloadData: reloadVinDetails,
  } = usePerformRequest<VinAPIResponse>({
    method: "GET",
    route: Endpoints.GetVehicleDetailsWithVIN(request?.vin as string),
  });

  useEffect(() => {
    if (r && r.data && r.data.id) {
      reloadVinDetails();
    }
  }, [r]);

  const [isVehicleModalOpen, { open: OpenVehicleModal, close: CloseVehicleModal }] =
    useDisclosure(false);
  const [
    isDocumentFormModalOpen,
    { open: OpenDocumentFormModal, close: CloseDocumentFormModal },
  ] = useDisclosure(false);

  const [documentToGenerate, setDocumentToGenerate] = useState<
    VehicleDocumentTypes | "unset"
  >("unset");
  const [isGeneratingDocument, setGeneratingDocument] = useState<boolean>(false);
  const [isDocumentListOpened, setDocumentListOpened] = useState<boolean>(false);

  const documentButtonProps: ButtonProps = {
    color: Colors.BluePrimary,
    variant: "subtle",
    fullWidth: true,
    className: "btn",
    disabled: isGeneratingDocument,
  };
  const StartGenerateDocument = async (docType: VehicleDocumentTypes) => {
    setGeneratingDocument(true);

    const getData = () => {
      const d: { doc: VehicleDocumentTypes; form: UseFormReturnType<any> }[] = [
        { doc: "mv", form: mvForm },
        { doc: "buyers-guide", form: buyersGuideForm },
        { doc: "odometer-disclosure-statement", form: odometerDisclosureForm },
        { doc: "bill-of-sale", form: billOfSaleForm },
        { doc: "buyers-order", form: buyersOrderForm },
      ];
      let data = d.find((dd) => dd.doc === docType)?.form.values;
      return data;
    };
    const doc = await generateDocument({
      docType,
      data: getData(),
      callback: () => {
        setGeneratingDocument(false);
      },
    });
    setGeneratingDocument(false);
    if (doc) {
      const url = window.URL.createObjectURL(new Blob([doc?.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${docType}-${request?.vin}.pdf`); // File name
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const mvForm = useForm({
    initialValues: {
      vin: "",
      model: "",
      make: "",
      year: "",
      color: "",
      style: "",
      mileage: "",
    },
    validate: {
      vin: (value) => (value && validateVIN(value) ? null : "Enter a valid VIN"),
      model: (value) => (value && value.length > 1 ? null : "Enter vehicle model"),
      make: (value) => (value && value.length > 1 ? null : "Enter vehicle make"),
      year: (value) => (value && value.length > 1 ? null : "Enter vehicle year"),
      color: (value) => (value && value.length > 1 ? null : "Enter vehicle color"),
      mileage: (value) => (value && value.length > 0 ? null : "Enter vehicle mileage"),
      style: (value) => (value && value.length > 1 ? null : "Enter vehicle style"),
    },
  });
  const buyersGuideForm = useForm({
    initialValues: {
      vin: "",
      model: "",
      make: "",
      year: "",
      color: "",
      name: "",
      email: "",
      address: "",
      phone: "",
    },
    validate: {
      vin: (value) => (value && validateVIN(value) ? null : "Enter a valid VIN"),
      model: (value) => (value && value.length > 1 ? null : "Enter vehicle model"),
      make: (value) => (value && value.length > 1 ? null : "Enter vehicle make"),
      year: (value) => (value && value.length > 1 ? null : "Enter vehicle year"),
      color: (value) => (value && value.length > 1 ? null : "Enter vehicle color"),
      name: (value) => (value && value.length > 1 ? null : "Enter owner name"),
      email: (value) => (validateEmail(value) ? null : "Enter owner email"),
      address: (value) => (value && value.length > 1 ? null : "Enter owner address"),
      phone: (value) => (value && value.length > 1 ? null : "Enter owner phone"),
    },
  });
  const odometerDisclosureForm = useForm({
    initialValues: {
      vin: "",
      model: "",
      make: "",
      year: "",
      body_style: "",
      email: "",
      transferee_name: "",
      transferor_name: "",
      buyer_city: "",
      seller_city: "Atlanta",
      buyer_state: "",
      seller_state: "Georgia",
      buyer_zip: "",
      seller_zip: "30337",
      transferee_address: "",
      transferor_address: "4854 OLD NATIONAL WY STE 242",
      mileage: "",
    },
    validate: {
      vin: (value) => (value && validateVIN(value) ? null : "Enter a valid VIN"),
      model: (value) => (value && value.length > 1 ? null : "Enter vehicle model"),
      make: (value) => (value && value.length > 1 ? null : "Enter vehicle make"),
      year: (value) => (value && value.length > 1 ? null : "Enter vehicle year"),
      body_style: (value) => (value && value.length > 1 ? null : "Enter vehicle style"),
      email: (value) => (validateEmail(value) ? null : "Enter email"),
      transferee_name: (value) =>
        value && value.length > 1 ? null : "Enter transferee name",
      transferor_name: (value) =>
        value && value.length > 1 ? null : "Enter transferror name",
      buyer_city: (value) => (value && value.length > 1 ? null : "Enter buyer city"),
      seller_city: (value) => (value && value.length > 1 ? null : "Enter seller city"),
      buyer_state: (value) => (value && value.length > 1 ? null : "Enter buyer state"),
      seller_state: (value) => (value && value.length > 1 ? null : "Enter seller state"),
      buyer_zip: (value) => (value && value.length > 1 ? null : "Enter buyer zip"),
      seller_zip: (value) => (value && value.length > 1 ? null : "Enter seller zip"),

      transferee_address: (value) =>
        value && value.length > 1 ? null : "Enter transferee address",
      transferor_address: (value) =>
        value && value.length > 1 ? null : "Enter transferor address",
      mileage: (value) => (value && value.length > 0 ? null : "Enter vehicle mileage"),
    },
  });
  const billOfSaleForm = useForm({
    initialValues: {
      mileage: "",
      vin: "",
      dealer_name: "FlipCars4Cash",
      dealer_address: "4854 OLD NATIONAL WY STE 242",
      dealer_city: "Atlanta",
      model: "",
      dealer_state: "Georgia",
      dealer_zip: "30337",
      buyer_name: "",
      buyer_city: "",
      buyer_state: "",
      buyer_address: "",
      buyer_zip: "",
      make: "",
      year: "",
    },
    validate: {
      mileage: (value) => (value && value.length > 0 ? null : "Enter vehicle mileage"),
      vin: (value) => (value && validateVIN(value) ? null : "Enter a valid VIN"),
      dealer_name: (value) => (value && value.length > 1 ? null : "Enter dealer name"),
      dealer_address: (value) =>
        value && value.length > 1 ? null : "Enter dealer address",
      dealer_city: (value) => (value && value.length > 1 ? null : "Enter dealer city"),
      model: (value) => (value && value.length > 1 ? null : "Enter vehicle model"),
      dealer_state: (value) => (value && value.length > 1 ? null : "Enter dealer state"),
      dealer_zip: (value) => (value && value.length > 1 ? null : "Enter dealer zip"),
      buyer_name: (value) => (value && value.length > 1 ? null : "Enter buyer name"),
      buyer_city: (value) => (value && value.length > 1 ? null : "Enter buyer city"),
      buyer_state: (value) => (value && value.length > 1 ? null : "Enter buyer state"),
      buyer_address: (value) =>
        value && value.length > 1 ? null : "Enter buyer address",
      buyer_zip: (value) => (value && value.length > 1 ? null : "Enter buyer zip"),
      make: (value) => (value && value.length > 1 ? null : "Enter vehicle make"),
      year: (value) => (value && value.length > 1 ? null : "Enter vehicle year"),
    },
  });
  const buyersOrderForm = useForm({
    initialValues: {
      vin: "",
      model: "",
      make: "",
      year: "",
      dealer_name: "FlipCars4Cash",
      buyer_name: "",
      buyer_city: "",
      dealer_city: "Atlanta",
      buyer_state: "",
      dealer_state: "Georgia",
      buyer_zip: "",
      dealer_zip: "30337",
      dealer_address: "4854 OLD NATIONAL WY STE 242",
      buyer_address: "",
      mileage: "",
      body_style: "",
    },
    validate: {
      vin: (value) => (value && validateVIN(value) ? null : "Enter a valid VIN"),
      model: (value) => (value && value.length > 1 ? null : "Enter vehicle model"),
      make: (value) => (value && value.length > 1 ? null : "Enter vehicle make"),
      year: (value) => (value && value.length > 1 ? null : "Enter vehicle year"),
      dealer_name: (value) => (value && value.length > 1 ? null : "Enter dealer name"),
      buyer_name: (value) => (value && value.length > 1 ? null : "Enter buyer name"),
      buyer_city: (value) => (value && value.length > 1 ? null : "Enter buyer city"),
      dealer_city: (value) => (value && value.length > 1 ? null : "Enter dealer city"),
      buyer_state: (value) => (value && value.length > 1 ? null : "Enter buyer state"),
      dealer_state: (value) => (value && value.length > 1 ? null : "Enter dealer state"),
      buyer_zip: (value) => (value && value.length > 1 ? null : "Enter buyer zip"),
      dealer_zip: (value) => (value && value.length > 1 ? null : "Enter dealer zip"),
      dealer_address: (value) =>
        value && value.length > 1 ? null : "Enter dealer address",
      buyer_address: (value) =>
        value && value.length > 1 ? null : "Enter buyer address",
      mileage: (value) => (value && value.length > 0 ? null : "Enter vehicle mileage"),
      body_style: (value) => (value && value.length > 1 ? null : "Enter body style"),
    },
  });

  useEffect(() => {
    if (rVin && rVin.Results.length > 0) {
      const make = getVehicleValueFromVinVariable("Make", rVin.Results)?.Value ?? "";
      const model = getVehicleValueFromVinVariable("Model", rVin.Results)?.Value ?? "";
      const year =
        getVehicleValueFromVinVariable("Model Year", rVin.Results)?.Value ?? "";
      const style =
        getVehicleValueFromVinVariable("Body Class", rVin.Results)?.Value ?? "";

      mvForm.setValues({
        model,
        make,
        year,
        style,
      });
      buyersGuideForm.setValues({
        model,
        make,
        year,
      });
      odometerDisclosureForm.setValues({
        model,
        make,
        year,
        body_style: style,
        transferor_name: "FlipCars4Cash",
        seller_state: "Georgia",
      });
      billOfSaleForm.setValues({
        model,
        make,
        year,
        dealer_name: "FlipCars4Cash",
        dealer_state: "Georgia",
      });
      buyersOrderForm.setValues({
        model,
        make,
        year,
        body_style: style,
        dealer_name: "FlipCars4Cash",
        dealer_state: "Georgia",
      });
    }
    if (request && request.id) {
      const { previousOwner, color, vin, odometer } = request;
      mvForm.setValues({
        color: color ?? "",
        vin: vin ?? "",
        mileage: odometer ?? "",
      });
      buyersGuideForm.setValues({
        color: color ?? "",
        vin: vin ?? "",
        name: previousOwner?.name ?? "",
        email: previousOwner?.email ?? "",
        phone: previousOwner?.phone ?? "",
      });
      odometerDisclosureForm.setValues({
        vin: vin ?? "",
        mileage: odometer ?? "",
      });
      billOfSaleForm.setValues({
        vin: vin ?? "",
        mileage: odometer ?? "",
      });
      buyersOrderForm.setValues({
        vin: vin ?? "",
        mileage: odometer ?? "",
      });
    }
  }, [isLoadingVinDetails, request]);

  const formInputProps = {
    spellCheck: false,
  };
  return (
    <AnimateOnLoad>
      <Container maxWidth="lg">
        {isLoadingRequest ? (
          <MegaLoader />
        ) : (
          <>
            {request && request.id ? (
              <div className="single-vehicle-container">
                <Text className="component-header" tt="capitalize">
                  {request.year} {request.manufacturer} {request.model}{" "}
                </Text>
                <TabularData
                  headers={["Email", "Name", "Phone", "Manufacturer", "Model", "Year"]}
                >
                  <Table.Tbody>
                    <Table.Tr>
                      <Table.Td key={request.previousOwner.email}>
                        {request.previousOwner.email}
                      </Table.Td>
                      <Table.Td key={request.previousOwner.name}>
                        {request.previousOwner.name}
                      </Table.Td>
                      <Table.Td key={request.previousOwner.phone}>
                        {request.previousOwner.phone}
                      </Table.Td>
                      <Table.Td key={request.manufacturer}>
                        {request.manufacturer}
                      </Table.Td>
                      <Table.Td key={request.model}>{request.model}</Table.Td>
                      <Table.Td key={request.year}>{request.year}</Table.Td>
                    </Table.Tr>
                  </Table.Tbody>
                </TabularData>
                <br />
                <TabularData
                  headers={[
                    "VIN",
                    "Driveable",
                    "Request Date",
                    "Purchase Date",
                    "Amount",
                  ]}
                >
                  <Table.Tbody>
                    <Table.Tr>
                      <Table.Td key={request.vin} c={Colors.BluePrimary}>
                        {request.vin}
                      </Table.Td>
                      <Table.Td
                        fw={600}
                        key={"driveable"}
                        c={request.driveable ? Colors.GreenPrimary : Colors.RedPrimary}
                      >
                        {request.driveable ? "Yes" : "No"}
                      </Table.Td>
                      <Table.Td key={request.createdAt}>
                        {dayjs(request.createdAt).format("ddd MMM DD YYYY")}
                      </Table.Td>
                      <Table.Td key={request.purchaseDate}>
                        {dayjs(request.createdAt).format("ddd MMM DD YYYY")}
                      </Table.Td>
                      <Table.Td key={request.amount} c={Colors.BluePrimary} fw={600}>
                        ${getFinancialValueFromNumeric(request.amount)}
                      </Table.Td>
                    </Table.Tr>
                  </Table.Tbody>
                </TabularData>
                <Divider mt={30} mb={20} />
                <div className="flex-row align-center">
                  <Text mr={15} className="component-header">
                    VIN Details
                  </Text>
                  <Button
                    disabled={isLoadingVinDetails}
                    mr={10}
                    onClick={() => {
                      OpenVehicleModal();
                    }}
                    variant="outline"
                    color={Colors.BluePrimary}
                    radius="50px"
                  >
                    Update Inventory
                  </Button>
                  <Popover
                    opened={isDocumentListOpened}
                    onChange={setDocumentListOpened}
                    width={300}
                    position="bottom"
                    withArrow
                    shadow="md"
                  >
                    <Popover.Target>
                      <Button
                        disabled={isLoadingVinDetails || request === undefined}
                        onClick={() => setDocumentListOpened((o) => !o)}
                        radius="50px"
                        color={Colors.GreenPrimary}
                      >
                        Generate Document
                      </Button>
                    </Popover.Target>

                    <Popover.Dropdown>
                      <div className="flex-col document-buttons">
                        {VehicleDocuments.map((document) => {
                          return (
                            <Button
                              {...documentButtonProps}
                              onClick={() => {
                                setDocumentToGenerate(document);
                                OpenDocumentFormModal();
                                setDocumentListOpened(false);
                              }}
                              tt="capitalize"
                            >
                              {getDocumentTitle(document)}
                            </Button>
                          );
                        })}
                      </div>
                    </Popover.Dropdown>
                  </Popover>
                  <Button
                    disabled={isLoadingVinDetails}
                    ml={10}
                    onClick={async () => {
                      const canShare =
                        // navigator && navigator.canShare && navigator.canShare();
                        true;
                      if (navigator.share) {
                        try {
                          await navigator.share({
                            text: "Check out this Vehicle!",
                            url: `https://flipcars4cash.com/vehicle/${vehicleID}`,
                            title: "Share Vehicle Information",
                          });
                        } catch (error) {
                          DefaultErrorNotification("Vehicle details cannot be shared!");
                        }
                      } else {
                        DefaultErrorNotification("Vehicle details cannot be shared lol");
                      }
                    }}
                    variant="filled"
                    leftSection={<IconShare size={15} />}
                    color={Colors.BluePrimary}
                    radius="50px"
                  >
                    Share
                  </Button>
                </div>
                {documentToGenerate && documentToGenerate !== "unset" && (
                  <Modal
                    centered
                    title={getDocumentTitle(documentToGenerate)}
                    opened={isDocumentFormModalOpen}
                    styles={{
                      title: {
                        textTransform: "capitalize",
                      },
                    }}
                    onClose={() => {
                      CloseDocumentFormModal();
                      setDocumentToGenerate("unset");
                    }}
                    size={"550px"}
                  >
                    <div className="generate-form-modal">
                      {documentToGenerate === "mv" && (
                        <>
                          <TextInput
                            w="100%"
                            {...mvForm.getInputProps("vin")}
                            label="VIN"
                            mb={15}
                            placeholder="Enter VIN"
                            {...formInputProps}
                          />
                          <div className="flex-row align-center form-row justify-between width-100">
                            <TextInput
                              w="48%"
                              {...mvForm.getInputProps("mileage")}
                              label="Mileage"
                              placeholder="Enter Mileage"
                              {...formInputProps}
                            />
                            <TextInput
                              w="48%"
                              {...mvForm.getInputProps("model")}
                              label="Model"
                              placeholder="Enter Model"
                              {...formInputProps}
                            />
                          </div>
                          <div className="flex-row align-center form-row justify-between width-100">
                            <TextInput
                              w="48%"
                              {...mvForm.getInputProps("make")}
                              label="Make"
                              placeholder="Enter Make"
                              {...formInputProps}
                            />
                            <TextInput
                              w="48%"
                              {...mvForm.getInputProps("year")}
                              label="Year"
                              placeholder="Enter Year"
                              {...formInputProps}
                            />
                          </div>
                          <div className="flex-row align-center form-row justify-between width-100">
                            <TextInput
                              w="48%"
                              {...mvForm.getInputProps("color")}
                              label="Color"
                              placeholder="Enter Color"
                              {...formInputProps}
                            />
                            <TextInput
                              w="48%"
                              {...mvForm.getInputProps("style")}
                              label="Style"
                              placeholder="Enter Style"
                              {...formInputProps}
                            />
                          </div>
                        </>
                      )}
                      {documentToGenerate === "odometer-disclosure-statement" && (
                        <>
                          <TextInput
                            w="100%"
                            {...odometerDisclosureForm.getInputProps("vin")}
                            label="VIN"
                            placeholder="Enter VIN"
                            {...formInputProps}
                            mb={20}
                          />
                          <div className="flex-row align-center form-row justify-between width-100">
                            <TextInput
                              w="48%"
                              {...odometerDisclosureForm.getInputProps("mileage")}
                              label="Vehicle Milage"
                              placeholder="Enter Mileage"
                              {...formInputProps}
                            />
                            <TextInput
                              w="48%"
                              type="email"
                              {...odometerDisclosureForm.getInputProps("email")}
                              label="Transferee email"
                              placeholder="Enter Transferee email"
                              {...formInputProps}
                            />
                          </div>
                          <div className="flex-row align-center form-row justify-between width-100">
                            <TextInput
                              w="48%"
                              {...odometerDisclosureForm.getInputProps("model")}
                              label="Model"
                              placeholder="Enter Model"
                              {...formInputProps}
                            />
                            <TextInput
                              w="48%"
                              {...odometerDisclosureForm.getInputProps("make")}
                              label="Make"
                              placeholder="Enter Make"
                              {...formInputProps}
                            />
                          </div>
                          <div className="flex-row align-center form-row justify-between width-100">
                            <TextInput
                              w="48%"
                              {...odometerDisclosureForm.getInputProps("year")}
                              label="Year"
                              placeholder="Enter Year"
                              {...formInputProps}
                            />
                            <TextInput
                              w="48%"
                              {...odometerDisclosureForm.getInputProps("body_style")}
                              label="Body Style"
                              placeholder="Enter Body Style"
                              {...formInputProps}
                            />
                          </div>
                          <div className="flex-row align-center form-row justify-between width-100">
                            <TextInput
                              w="48%"
                              {...odometerDisclosureForm.getInputProps("transferee_name")}
                              label="Transferee name"
                              placeholder="Enter name"
                              {...formInputProps}
                            />
                            <TextInput
                              w="48%"
                              {...odometerDisclosureForm.getInputProps("transferor_name")}
                              label="Transferor name"
                              placeholder="Enter name"
                              {...formInputProps}
                            />
                          </div>
                          <div className="flex-row align-center form-row justify-between width-100">
                            <TextInput
                              w="48%"
                              {...odometerDisclosureForm.getInputProps("buyer_state")}
                              label="Buyer state"
                              placeholder="Enter Buyer state"
                              {...formInputProps}
                              mb={20}
                            />
                            <TextInput
                              w="48%"
                              {...odometerDisclosureForm.getInputProps("buyer_city")}
                              label="Buyer city"
                              placeholder="Enter Buyer city"
                              {...formInputProps}
                              mb={20}
                            />
                          </div>
                          <div className="flex-row align-center form-row justify-between width-100">
                            <TextInput
                              w="48%"
                              {...odometerDisclosureForm.getInputProps("seller_state")}
                              label="Seller state"
                              placeholder="Enter Seller state"
                              {...formInputProps}
                              mb={20}
                            />
                            <TextInput
                              w="48%"
                              {...odometerDisclosureForm.getInputProps("seller_city")}
                              label="Seller city"
                              placeholder="Enter Seller city"
                              {...formInputProps}
                              mb={20}
                            />
                          </div>
                          <div className="flex-row align-center form-row justify-between width-100">
                            <TextInput
                              w="48%"
                              {...odometerDisclosureForm.getInputProps("buyer_zip")}
                              label="Buyer zip"
                              placeholder="Enter Buyer zip"
                              {...formInputProps}
                              mb={20}
                            />
                            <TextInput
                              w="48%"
                              type="email"
                              {...odometerDisclosureForm.getInputProps("seller_zip")}
                              label="Seller zip"
                              placeholder="Enter Seller zip"
                              {...formInputProps}
                              mb={20}
                            />
                          </div>
                          <Textarea
                            mb={20}
                            w="100%"
                            {...odometerDisclosureForm.getInputProps(
                              "transferee_address"
                            )}
                            label="Transferee Address"
                            placeholder="Enter Transferee Address"
                            {...formInputProps}
                          />
                          <Textarea
                            mb={20}
                            w="100%"
                            {...odometerDisclosureForm.getInputProps(
                              "transferor_address"
                            )}
                            label="Transferor Address"
                            placeholder="Enter Transferor Address"
                            {...formInputProps}
                          />
                        </>
                      )}
                      {documentToGenerate === "bill-of-sale" && (
                        <>
                          <TextInput
                            w="100%"
                            {...billOfSaleForm.getInputProps("vin")}
                            label="VIN"
                            placeholder="Enter VIN"
                            {...formInputProps}
                            mb={20}
                          />
                          <div className="flex-row align-center form-row justify-between width-100">
                            <TextInput
                              w="48%"
                              {...billOfSaleForm.getInputProps("mileage")}
                              label="Vehicle Milage"
                              placeholder="Enter Mileage"
                              {...formInputProps}
                            />
                            <TextInput
                              w="48%"
                              {...billOfSaleForm.getInputProps("year")}
                              label="Vehicle year"
                              placeholder="Enter year"
                              {...formInputProps}
                            />
                          </div>
                          <div className="flex-row align-center form-row justify-between width-100">
                            <TextInput
                              w="48%"
                              {...billOfSaleForm.getInputProps("model")}
                              label="Model"
                              placeholder="Enter Model"
                              {...formInputProps}
                            />
                            <TextInput
                              w="48%"
                              {...billOfSaleForm.getInputProps("make")}
                              label="Make"
                              placeholder="Enter Make"
                              {...formInputProps}
                            />
                          </div>

                          <div className="flex-row align-center form-row justify-between width-100">
                            <TextInput
                              w="48%"
                              {...billOfSaleForm.getInputProps("dealer_name")}
                              label="Dealer name"
                              placeholder="Enter name"
                              {...formInputProps}
                            />
                            <TextInput
                              w="48%"
                              {...billOfSaleForm.getInputProps("buyer_name")}
                              label="Buyer name"
                              placeholder="Enter name"
                              {...formInputProps}
                            />
                          </div>
                          <div className="flex-row align-center form-row justify-between width-100">
                            <TextInput
                              w="48%"
                              {...billOfSaleForm.getInputProps("buyer_state")}
                              label="Buyer state"
                              placeholder="Enter Buyer state"
                              {...formInputProps}
                              mb={20}
                            />
                            <TextInput
                              w="48%"
                              {...billOfSaleForm.getInputProps("buyer_city")}
                              label="Buyer city"
                              placeholder="Enter Buyer city"
                              {...formInputProps}
                              mb={20}
                            />
                          </div>
                          <div className="flex-row align-center form-row justify-between width-100">
                            <TextInput
                              w="48%"
                              {...billOfSaleForm.getInputProps("dealer_state")}
                              label="Dealer state"
                              placeholder="Enter Dealer state"
                              {...formInputProps}
                              mb={20}
                            />
                            <TextInput
                              w="48%"
                              {...billOfSaleForm.getInputProps("dealer_city")}
                              label="Dealer city"
                              placeholder="Enter Dealer city"
                              {...formInputProps}
                              mb={20}
                            />
                          </div>
                          <div className="flex-row align-center form-row justify-between width-100">
                            <TextInput
                              w="48%"
                              {...billOfSaleForm.getInputProps("buyer_zip")}
                              label="Buyer zip"
                              placeholder="Enter Buyer zip"
                              {...formInputProps}
                              mb={20}
                            />
                            <TextInput
                              w="48%"
                              type="email"
                              {...billOfSaleForm.getInputProps("dealer_zip")}
                              label="Dealer zip"
                              placeholder="Enter Dealer zip"
                              {...formInputProps}
                              mb={20}
                            />
                          </div>
                          <Textarea
                            mb={20}
                            w="100%"
                            {...billOfSaleForm.getInputProps("buyer_address")}
                            label="Buyer Address"
                            placeholder="Enter Buyer Address"
                            {...formInputProps}
                          />
                          <Textarea
                            mb={20}
                            w="100%"
                            {...billOfSaleForm.getInputProps("dealer_address")}
                            label="Dealer Address"
                            placeholder="Enter Dealer Address"
                            {...formInputProps}
                          />
                        </>
                      )}
                      {documentToGenerate === "buyers-guide" && (
                        <>
                          <TextInput
                            w="100%"
                            {...buyersGuideForm.getInputProps("vin")}
                            label="VIN"
                            placeholder="Enter VIN"
                            {...formInputProps}
                          />
                          <div className="flex-row align-center form-row justify-between width-100">
                            <TextInput
                              w="48%"
                              {...buyersGuideForm.getInputProps("model")}
                              label="Model"
                              placeholder="Enter Model"
                              {...formInputProps}
                            />
                            <TextInput
                              w="48%"
                              {...buyersGuideForm.getInputProps("make")}
                              label="Make"
                              placeholder="Enter Make"
                              {...formInputProps}
                            />
                          </div>
                          <div className="flex-row align-center form-row justify-between width-100">
                            <TextInput
                              w="48%"
                              {...buyersGuideForm.getInputProps("year")}
                              label="Year"
                              placeholder="Enter Year"
                              {...formInputProps}
                            />
                            <TextInput
                              w="48%"
                              {...buyersGuideForm.getInputProps("color")}
                              label="Color"
                              placeholder="Enter Color"
                              {...formInputProps}
                            />
                          </div>
                          <div className="flex-row align-center form-row justify-between width-100">
                            <TextInput
                              w="48%"
                              {...buyersGuideForm.getInputProps("name")}
                              label="Owner name"
                              placeholder="Enter Owner name"
                              {...formInputProps}
                            />
                            <TextInput
                              w="48%"
                              {...buyersGuideForm.getInputProps("phone")}
                              label="Owner phone"
                              placeholder="Enter Owner phone"
                              {...formInputProps}
                            />
                          </div>
                          <TextInput
                            w="100%"
                            type="email"
                            {...buyersGuideForm.getInputProps("email")}
                            label="Owner email"
                            placeholder="Enter Owner email"
                            {...formInputProps}
                            mb={20}
                          />
                          <Textarea
                            mb={20}
                            w="100%"
                            {...buyersGuideForm.getInputProps("address")}
                            label="Owner address"
                            placeholder="Enter Owner address"
                            {...formInputProps}
                          />
                        </>
                      )}

                      {documentToGenerate === "buyers-order" && (
                        <>
                          <div className="flex-row align-center form-row justify-between width-100">
                            <TextInput
                              w="48%"
                              {...buyersOrderForm.getInputProps("vin")}
                              label="VIN"
                              placeholder="Enter VIN"
                              {...formInputProps}
                            />
                            <TextInput
                              w="48%"
                              {...buyersOrderForm.getInputProps("body_style")}
                              label="Style"
                              placeholder="Enter Body Style"
                              {...formInputProps}
                            />
                          </div>
                          <div className="flex-row align-center form-row justify-between width-100">
                            <TextInput
                              w="48%"
                              {...buyersOrderForm.getInputProps("mileage")}
                              label="Vehicle Milage"
                              placeholder="Enter Mileage"
                              {...formInputProps}
                            />
                            <TextInput
                              w="48%"
                              {...buyersOrderForm.getInputProps("year")}
                              label="Vehicle year"
                              placeholder="Enter year"
                              {...formInputProps}
                            />
                          </div>
                          <div className="flex-row align-center form-row justify-between width-100">
                            <TextInput
                              w="48%"
                              {...buyersOrderForm.getInputProps("model")}
                              label="Model"
                              placeholder="Enter Model"
                              {...formInputProps}
                            />
                            <TextInput
                              w="48%"
                              {...buyersOrderForm.getInputProps("make")}
                              label="Make"
                              placeholder="Enter Make"
                              {...formInputProps}
                            />
                          </div>

                          <div className="flex-row align-center form-row justify-between width-100">
                            <TextInput
                              w="48%"
                              {...buyersOrderForm.getInputProps("dealer_name")}
                              label="Dealer name"
                              placeholder="Enter name"
                              {...formInputProps}
                            />
                            <TextInput
                              w="48%"
                              {...buyersOrderForm.getInputProps("buyer_name")}
                              label="Buyer name"
                              placeholder="Enter name"
                              {...formInputProps}
                            />
                          </div>
                          <div className="flex-row align-center form-row justify-between width-100">
                            <TextInput
                              w="48%"
                              {...buyersOrderForm.getInputProps("buyer_state")}
                              label="Buyer state"
                              placeholder="Enter Buyer state"
                              {...formInputProps}
                              mb={20}
                            />
                            <TextInput
                              w="48%"
                              {...buyersOrderForm.getInputProps("buyer_city")}
                              label="Buyer city"
                              placeholder="Enter Buyer city"
                              {...formInputProps}
                              mb={20}
                            />
                          </div>
                          <div className="flex-row align-center form-row justify-between width-100">
                            <TextInput
                              w="48%"
                              {...buyersOrderForm.getInputProps("dealer_state")}
                              label="Dealer state"
                              placeholder="Enter Dealer state"
                              {...formInputProps}
                              mb={20}
                            />
                            <TextInput
                              w="48%"
                              {...buyersOrderForm.getInputProps("dealer_city")}
                              label="Dealer city"
                              placeholder="Enter Dealer city"
                              {...formInputProps}
                              mb={20}
                            />
                          </div>
                          <div className="flex-row align-center form-row justify-between width-100">
                            <TextInput
                              w="48%"
                              {...buyersOrderForm.getInputProps("buyer_zip")}
                              label="Buyer zip"
                              placeholder="Enter Buyer zip"
                              {...formInputProps}
                              mb={20}
                            />
                            <TextInput
                              w="48%"
                              type="email"
                              {...buyersOrderForm.getInputProps("dealer_zip")}
                              label="Dealer zip"
                              placeholder="Enter Dealer zip"
                              {...formInputProps}
                              mb={20}
                            />
                          </div>
                          <Textarea
                            mb={20}
                            w="100%"
                            {...buyersOrderForm.getInputProps("buyer_address")}
                            label="Buyer Address"
                            placeholder="Enter Buyer Address"
                            {...formInputProps}
                          />
                          <Textarea
                            mb={20}
                            w="100%"
                            {...buyersOrderForm.getInputProps("dealer_address")}
                            label="Dealer Address"
                            placeholder="Enter Dealer Address"
                            {...formInputProps}
                          />
                        </>
                      )}
                      <div className="width-100 flex-row align-center justify-center">
                        <Button
                          color={Colors.GreenPrimary}
                          loading={isGeneratingDocument}
                          onClick={() => {
                            let form: UseFormReturnType<any> = {} as any;
                            switch (documentToGenerate) {
                              case "mv":
                                form = mvForm;
                                break;
                              case "buyers-guide":
                                form = buyersGuideForm;
                                break;
                              case "odometer-disclosure-statement":
                                form = odometerDisclosureForm;
                                break;
                              case "bill-of-sale":
                                form = billOfSaleForm;
                                break;
                              case "buyers-order":
                                form = buyersOrderForm;
                                break;
                            }

                            const { hasErrors } = form.validate();
                            if (!hasErrors) {
                              StartGenerateDocument(documentToGenerate);
                            }
                          }}
                          w={200}
                          radius="50px"
                        >
                          Get Template
                        </Button>
                      </div>
                    </div>
                  </Modal>
                )}
                <br />

                <div className="vin-details">
                  {isLoadingVinDetails ? (
                    <MegaLoader />
                  ) : (
                    <>
                      {rVin && rVin.Count && rVin.Results.length > 0 ? (
                        <>
                          {rVin.Results.filter(
                            (res) => res.Value && res.Value.length > 0
                          ).map((item) => {
                            return (
                              <div className="flex-row item align-center">
                                <Text className="label">{item.Variable}</Text>
                                <Text className="value">{item.Value}</Text>
                              </div>
                            );
                          })}
                        </>
                      ) : (
                        <DisplayAlert
                          title="Could not get details"
                          children={
                            <div className="flex-col">
                              <Text>
                                There was an error communicating with the VIN API!
                              </Text>
                              <Button
                                color={Colors.BluePrimary}
                                leftSection={<IconReload size={17} />}
                                w={160}
                                onClick={reloadVinDetails}
                              >
                                Try Again
                              </Button>
                            </div>
                          }
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
            ) : (
              <DisplayAlert
                title="No Vehicle Found"
                children={
                  <div className="flex-col">
                    <Text>No existing vehicle matches this request!</Text>
                    <Button
                      onClick={() => {
                        navigate("/dashboard/inventory");
                      }}
                      color={Colors.BluePrimary}
                      leftSection={<IconChevronLeft size={17} />}
                      w={230}
                    >
                      Back to Inventory
                    </Button>
                  </div>
                }
              />
            )}
          </>
        )}
        {request && (
          <VehicleForm
            action="update"
            isFormOpen={isVehicleModalOpen}
            OnComplete={() => {
              reloadVinDetails();
              reloadRequest();
            }}
            CloseForm={CloseVehicleModal}
            OpenForm={OpenVehicleModal}
            vehicle={{
              id: request?.id,
              previousOwner: {
                email: request.previousOwner.email,
                phone: request.previousOwner.phone,
                name: request.previousOwner.name,
              },
              manufacturer: request.manufacturer,
              images: request.images,
              createdAt: request.createdAt,
              model: request.model,
              amount: request.amount,
              odometer: request.odometer,
              purchaseDate: request.purchaseDate,
              notes: request.notes,
              year: request.year,
              color: request.color,
              driveable: request.driveable,
              vin: request.vin,
            }}
          />
        )}
      </Container>
    </AnimateOnLoad>
  );
};

export default SingleVehicle;
